import type { Vehicle, ApiResponse, VehicleAdjustment, RentAddition } from '../types'
import { callApi, ensureLogin } from './tcbClient'

type OperatorInfo = {
  userId?: string
  username?: string
  nickName?: string
  role?: string
  info?: string
}

const readOperatorFromStorage = (): OperatorInfo => {
  try {
    if (typeof window === 'undefined') return {}
    const raw = window.localStorage.getItem('auth-storage')
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    const user = parsed?.state?.user || parsed?.user
    if (!user) return {}
    return {
      userId: user.id,
      username: user.username,
      nickName: user.nickname || user.nickName,
      role: user.role,
      info: user.info
    }
  } catch {
    return {}
  }
}

const pickOperatorFromPayload = (payload: any): OperatorInfo => {
  if (!payload || typeof payload !== 'object') return {}
  return {
    userId: payload?.operatorId || payload?.userId || payload?.makerId || payload?.cashierId || payload?.bossId || payload?.accountantId,
    username: payload?.username,
    nickName: payload?.operatorName || payload?.createdBy || payload?.cashierName || payload?.bossName || payload?.accountantName || payload?.nickName,
    role: payload?.role,
    info: payload?.info
  }
}

function enrichOperatorFields(payload: any): any {
  const byPayload = pickOperatorFromPayload(payload)
  const byStorage = readOperatorFromStorage()
  const operator: OperatorInfo = {
    userId: byPayload.userId || byStorage.userId,
    username: byPayload.username || byStorage.username,
    nickName: byPayload.nickName || byStorage.nickName,
    role: byPayload.role || byStorage.role,
    info: byPayload.info || byStorage.info
  }
  const next: any = { ...payload }
  if (!next.createdBy) {
    next.createdBy = operator.nickName || operator.username || operator.userId || 'system'
  }
  if (!next.makerId) {
    next.makerId = operator.userId || operator.username || next.createdBy
  }
  return next
}

class SimpleDirectCloudApi {
  async init(): Promise<{ success: boolean; error?: string }> {
    try {
      await ensureLogin()
      return { success: true }
    } catch (err: any) {
      return { success: false, error: err?.message }
    }
  }

  async checkConnection(): Promise<boolean> {
    try {
      await ensureLogin()
      return true
    } catch {
      return false
    }
  }

  // ========== Vehicles ==========

  async getAllVehicles(): Promise<ApiResponse<Vehicle[]>> {
    return callApi('getAllVehicles')
  }

  async getVehiclesByPage(status: string, page: number, pageSize: number): Promise<ApiResponse<Vehicle[]>> {
    return callApi('getVehiclesByPage', { status, page, pageSize })
  }

  async searchVehicles(keyword: string): Promise<ApiResponse<Vehicle[]>> {
    return callApi('searchVehicles', { keyword })
  }

  async addVehicle(vehicleData: Omit<Vehicle, 'id' | 'createdAt' | 'updatedAt'>): Promise<ApiResponse<Vehicle>> {
    const operator = readOperatorFromStorage()
    return callApi('addVehicle', { vehicleData: { ...vehicleData, _actor: operator } })
  }

  async updateVehicle(id: string, updates: Partial<Vehicle>): Promise<ApiResponse> {
    const operator = readOperatorFromStorage()
    return callApi('updateVehicle', { id, updates: { ...updates, _actor: operator } })
  }

  async deleteVehicle(id: string): Promise<ApiResponse> {
    const operator = readOperatorFromStorage()
    return callApi('deleteVehicle', { id, actor: operator })
  }

  async getStatistics(): Promise<ApiResponse<any>> {
    return callApi('getStatistics')
  }

  // ========== Users ==========

  async getUserByNickName(nickName: string): Promise<ApiResponse<any>> {
    return callApi('getUserByNickName', { nickName })
  }

  async getAvailableUsers(): Promise<ApiResponse<any[]>> {
    return callApi('getAvailableUsers')
  }

  async getUserByIdentity(value: string): Promise<ApiResponse<any>> {
    return callApi('getUserByIdentity', { value })
  }

  async addUserProfile(data: { uid?: string; username?: string; nickName?: string; role?: string }): Promise<ApiResponse<any>> {
    return callApi('addUserProfile', { data })
  }

  // ========== Receipts ==========

  async addReceipt(data: any): Promise<ApiResponse<any>> {
    const enriched = enrichOperatorFields(data)
    return callApi('addReceipt', { data: enriched })
  }

  async updateReceipt(id: string, updates: any): Promise<ApiResponse<any>> {
    return callApi('updateReceipt', { id, updates })
  }

  async getReceipts(status?: string): Promise<ApiResponse<any[]>> {
    return callApi('getReceipts', { status })
  }

  async deleteReceipt(id: string): Promise<ApiResponse> {
    return callApi('deleteReceipt', { id })
  }

  // ========== Vehicle Adjustments ==========

  async getVehicleAdjustments(vehicleId?: string): Promise<ApiResponse<VehicleAdjustment[]>> {
    return callApi('getVehicleAdjustments', { vehicleId })
  }

  async addVehicleAdjustment(data: VehicleAdjustment): Promise<ApiResponse<VehicleAdjustment>> {
    return callApi('addVehicleAdjustment', { data })
  }

  async updateVehicleAdjustment(id: string, updates: Partial<VehicleAdjustment>): Promise<ApiResponse> {
    return callApi('updateVehicleAdjustment', { id, updates })
  }

  async deleteVehicleAdjustment(id: string): Promise<ApiResponse> {
    return callApi('deleteVehicleAdjustment', { id })
  }

  // ========== Vehicle Rent Additions ==========

  async getVehicleRentAdditions(vehicleId?: string): Promise<ApiResponse<RentAddition[]>> {
    return callApi('getVehicleRentAdditions', { vehicleId })
  }

  async addVehicleRentAddition(data: RentAddition): Promise<ApiResponse<RentAddition>> {
    return callApi('addVehicleRentAddition', { data })
  }

  async updateVehicleRentAddition(id: string, updates: Partial<RentAddition>): Promise<ApiResponse> {
    return callApi('updateVehicleRentAddition', { id, updates })
  }

  async deleteVehicleRentAddition(id: string): Promise<ApiResponse> {
    return callApi('deleteVehicleRentAddition', { id })
  }

  // ========== Lease Forms ==========

  async addLeaseForm(data: any): Promise<ApiResponse<any>> {
    const enriched = enrichOperatorFields(data)
    return callApi('addLeaseForm', { data: enriched })
  }

  async getLeaseForms(status?: string): Promise<ApiResponse<any[]>> {
    return callApi('getLeaseForms', { status })
  }

  async updateLeaseForm(id: string, updates: any): Promise<ApiResponse<any>> {
    return callApi('updateLeaseForm', { id, updates })
  }

  async deleteLeaseForm(id: string, revertToRented?: boolean, vehicleId?: string): Promise<ApiResponse<any>> {
    const operator = readOperatorFromStorage()
    return callApi('deleteLeaseForm', { id, revertToRented: !!revertToRented, vehicleId, actor: operator })
  }

  async getPaymentRecords(): Promise<ApiResponse<any[]>> {
    return callApi('getPaymentRecords')
  }

  // ========== Rent Adjust Requests ==========

  async addRentAdjustRequest(data: any): Promise<ApiResponse<any>> {
    const enriched = enrichOperatorFields(data)
    return callApi('addRentAdjustRequest', { data: enriched })
  }

  async getRentAdjustRequests(status?: string): Promise<ApiResponse<any[]>> {
    return callApi('getRentAdjustRequests', { status })
  }

  async updateRentAdjustRequest(id: string, updates: any): Promise<ApiResponse<any>> {
    return callApi('updateRentAdjustRequest', { id, updates })
  }

  // ========== Operation Logs ==========

  async getOperationLogs(limit = 500): Promise<ApiResponse<any[]>> {
    return callApi('getOperationLogs', { limit })
  }

  // ========== Financial Calculations (Server-side) ==========

  async getAllVehiclesWithOutstanding(): Promise<ApiResponse<Vehicle[]>> {
    return callApi('getAllVehiclesWithOutstanding')
  }

  async getTotalPaidReceiptIncome(): Promise<ApiResponse<number>> {
    return callApi('getTotalPaidReceiptIncome')
  }

  async getOutstandingAtDate(vehicleId: string, asOf: string): Promise<ApiResponse<{ outstanding: number; expected: number; paid: number }>> {
    return callApi('getOutstandingAtDate', { vehicleId, asOf })
  }

  async batchOutstandingAtDate(asOf: string, vehicleIds?: string[]): Promise<ApiResponse<Record<string, { outstanding: number; expected: number; paid: number }>>> {
    return callApi('batchOutstandingAtDate', { asOf, vehicleIds })
  }

  async getVehicleOutstanding(vehicleId: string): Promise<ApiResponse<{
    outstanding: number
    expectedTotal: number
    paidTotal: number
    adjustmentDelta: number
    leaseStart: string
    dueDate: string | null
  }>> {
    return callApi('getVehicleOutstanding', { vehicleId })
  }

  async computeChargeAmount(vehicleId: string, startDate: string, endDate: string): Promise<ApiResponse<{
    amount: number
    days: number
    dailyRate: number
    monthlyRate: number
    annualAmount: number
  }>> {
    return callApi('computeChargeAmount', { vehicleId, startDate, endDate })
  }

  async getVehicleBillingInfo(vehicleId: string): Promise<ApiResponse<{
    outstanding: number
    expectedTotal: number
    paidTotal: number
    leaseStart: string
    dueDate: string | null
    periods: Array<{ key: string; start: string; end: string; expected: number; paid: number; deficit: number }>
    suggestedChargeRange: { start: string; end: string; amount: number } | null
    previousChargeEnd: string | null
  }>> {
    return callApi('getVehicleBillingInfo', { vehicleId })
  }
}

export const simpleDirectCloudApi = new SimpleDirectCloudApi()
export type { ApiResponse }
